"use client";
import { useState, useRef, useEffect } from 'react'
import Image from 'next/image'
import { ChevronRight } from 'lucide-react'
import useEmblaCarousel from 'embla-carousel-react'
import { cn } from '@/lib/utils'

interface Props {
  productName: string
  galleryImages: { id: string; image_url: string; label: string }[]
  hoveredImage: string | null
}

export function ProductGallery({ productName, galleryImages, hoveredImage: propHoveredImage }: Props) {
  const [activeImage, setActiveImage] = useState<string | null>(null)
  const [hoveredImage, setHoveredImage] = useState<string | null>(null)
  const [brokenImages, setBrokenImages] = useState<Set<string>>(new Set())
  const isHovering = useRef(false)

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, duration: 25 })
  const [thumbViewportRef, thumbEmblaApi] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    dragFree: true,
    align: 'center'
  })

  const filteredGallery = galleryImages.filter(img => !brokenImages.has(img.image_url))

  // Sync embla with active/hovered image
  useEffect(() => {
    if (!emblaApi) return
    const targetImage = propHoveredImage || hoveredImage || activeImage || filteredGallery[0]?.image_url
    if (!targetImage) return
    
    const idx = filteredGallery.findIndex(img => img.image_url === targetImage)
    if (idx !== -1) emblaApi.scrollTo(idx)
  }, [emblaApi, activeImage, hoveredImage, propHoveredImage, filteredGallery])

  // Sync thumbnails with embla
  useEffect(() => {
    if (!emblaApi) return
    const onSelect = () => {
      if (isHovering.current) return
      const idx = emblaApi.selectedScrollSnap()
      const img = filteredGallery[idx]
      if (img) setActiveImage(img.image_url)
      if (thumbEmblaApi) thumbEmblaApi.scrollTo(idx)
    }
    emblaApi.on('select', onSelect)
    return () => emblaApi.off('select', onSelect)
  }, [emblaApi, filteredGallery, thumbEmblaApi])

  return (
    <div className="flex flex-col gap-4">
      <div className="relative aspect-square rounded-2xl overflow-hidden bg-white border border-border group/main">
        {/* Variant Hover Preview Overlay */}
        {propHoveredImage && !filteredGallery.some(img => img.image_url === propHoveredImage) && (
          <div className="absolute inset-0 z-30 bg-white animate-in fade-in duration-200">
             <img
              src={propHoveredImage}
              alt="Preview"
              className="h-full w-full object-contain"
            />
          </div>
        )}
        <div className="overflow-hidden h-full" ref={emblaRef}>
          <div className="flex h-full">
            {filteredGallery.map((img, idx) => (
              <div key={img.id} className="flex-[0_0_100%] min-w-0 h-full relative">
                <img
                  src={img.image_url}
                  alt={productName}
                  fetchPriority={idx === 0 ? "high" : "auto"}
                  className="h-full w-full object-contain"
                  onError={() => setBrokenImages(prev => new Set(prev).add(img.image_url))}
                />
              </div>
            ))}
            {filteredGallery.length === 0 && (
              <div className="flex-[0_0_100%] min-w-0 h-full relative flex items-center justify-center bg-muted/20">
                <p className="text-muted-foreground text-sm">Image not available</p>
              </div>
            )}
          </div>
        </div>

        {/* Navigation Arrows */}
        <button 
          onClick={() => emblaApi?.scrollPrev()}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 backdrop-blur shadow-sm flex items-center justify-center opacity-0 group-hover/main:opacity-100 transition-opacity z-20 hover:bg-white"
        >
          <ChevronRight className="h-5 w-5 rotate-180" />
        </button>
        <button 
          onClick={() => emblaApi?.scrollNext()}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 backdrop-blur shadow-sm flex items-center justify-center opacity-0 group-hover/main:opacity-100 transition-opacity z-20 hover:bg-white"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
      
      {/* Thumbnails */}
      <div className="overflow-hidden p-1" ref={thumbViewportRef}>
        <div className="flex gap-2 h-full">
          {filteredGallery.map((img, idx) => {
            const isSelected = activeImage === img.image_url || (!activeImage && idx === 0)
            const isHovered = hoveredImage === img.image_url
            
            return (
              <button
                key={img.id}
                onClick={() => {
                  setActiveImage(img.image_url)
                  setHoveredImage(null)
                }}
                onMouseEnter={() => {
                  isHovering.current = true
                  setHoveredImage(img.image_url)
                }}
                onMouseLeave={() => {
                  isHovering.current = false
                  setTimeout(() => { if (!isHovering.current) setHoveredImage(null) }, 50)
                }}
                className={cn(
                  "flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all relative group",
                  (isSelected || isHovered)
                    ? "border-brand-accent shadow-sm scale-105 z-10" 
                    : "border-transparent hover:border-muted-foreground/30"
                )}
              >
                <img
                  src={img.image_url}
                  alt={img.label}
                  className="h-full w-full object-cover group-hover:scale-110 transition-transform"
                />
                <div className={cn(
                  "absolute inset-0 transition-colors",
                  isSelected ? "bg-transparent" : "bg-black/5 group-hover:bg-black/0"
                )} />
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}
